import { useMemo, useState } from 'react'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { RoleType } from '../../models/Role'
import { useBuyAndPromoteInvitesMutation } from '../../store/auth/auth'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { addToast } from '../../store/toast/toastSlice'
import { readBuyResult } from './buyResult'
import { type SeatPurchasePreview, useSeatPurchasePreview } from './useSeatPurchasePreview'

const NOTHING_TO_BUY = { general: 0, sidecar: 0 }

export interface BuySeatTarget {
  invitationId: string
  seatType: RoleType
  email: string
}

export interface BuySeatCallbacks {
  onSeatsChanged: () => void
  onSent: () => void
  onGone: () => void
}

export interface BuySeatState {
  preview: SeatPurchasePreview
  error: string | null
  isConfirming: boolean
  isBought: boolean
  confirm: () => Promise<void>
}

export const useBuySeat = (target: BuySeatTarget | null, { onSeatsChanged, onSent, onGone }: BuySeatCallbacks): BuySeatState => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { trackingClient } = useTracking()
  const [buyAndPromoteInvites, { isLoading }] = useBuyAndPromoteInvitesMutation()
  const [error, setError] = useState<string | null>(null)
  const [seatBought, setSeatBought] = useState(false)

  const purchase = useMemo(
    () => (target ? { general: target.seatType === RoleType.SEAT ? 1 : 0, sidecar: target.seatType === RoleType.SIDECAR ? 1 : 0 } : NOTHING_TO_BUY),
    [target],
  )
  const preview = useSeatPurchasePreview(purchase)

  const confirm = async () => {
    if (!target) {
      return
    }

    setError(null)
    let verdict
    try {
      const response = await buyAndPromoteInvites({ accountId, invitationIds: [target.invitationId], purchase }).unwrap()
      verdict = readBuyResult(response, target.invitationId, target.seatType)
    } catch {
      verdict = readBuyResult(undefined, target.invitationId, target.seatType)
    }

    onSeatsChanged()

    if (verdict.bought) {
      trackingClient.track({ eventName: TrackEvent.SEAT_PURCHASED, eventDetails: { email: target.email, seatType: target.seatType } })
      setSeatBought(true)
    }

    if (verdict.ok) {
      dispatch(addToast({ variant: 'success', title: verdict.bought ? 'Seat purchased. Invitation sent' : 'Invitation sent' }))
      onSent()
      return
    }

    if (verdict.closesModal) {
      if (verdict.message) {
        dispatch(addToast({ variant: 'info', title: verdict.message }))
      }
      onGone()
      return
    }

    setError(verdict.message ?? null)
  }

  return { preview, error, isConfirming: isLoading, isBought: seatBought, confirm }
}
